import { useState } from 'react';
import { Phone } from 'lucide-react';

import { RequiredLabel } from '@/components/required-label';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';

import { useAgent } from '@/context/AgentContext';

import { useEventHandler } from '@/hooks/use-event-handler';
import { useToggle } from '@/hooks/use-toggle';

import { useDispatchAgentPhoneMutation } from '@/query/call.query';

import type { DispatchAgentRequest } from '@/types/call.type';

export const TestAgentPhone = () => {
  const { agent } = useAgent();

  const [state, setState] = useState<Record<string, string>>({});
  const { state: isTestCallOpen, toggle, close, open } = useToggle();

  const onOpen = () => {
    setState({});
    open();
  };

  useEventHandler({ name: 'agent:test:phone', eventHandler: onOpen });

  const mutation = useDispatchAgentPhoneMutation({
    onSuccess: () => {
      close();
    },
  });

  return (
    <div className="ml-auto flex items-center gap-2">
      <Sheet open={isTestCallOpen} onOpenChange={toggle}>
        <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto p-0">
          <SheetHeader className="p-4 w-full relative">
            <SheetTitle>Test call</SheetTitle>
          </SheetHeader>
          <div>
            <section className="bg-background flex flex-col items-center justify-center text-center">
              <div className="flex flex-col items-center justify-center h-full space-y-6 px-4 pb-10">
                <div className="text-center space-y-2">
                  <div className="relative w-20 h-20 mx-auto mb-4">
                    {/* Ripple circles */}
                    <div className="absolute inset-0 rounded-full border-2 border-primary/20 animate-ping"></div>
                    <div
                      className="absolute inset-0 rounded-full border-2 border-primary/15 animate-ping"
                      style={{ animationDelay: '0.25s' }}
                    ></div>
                    <div
                      className="absolute inset-0 rounded-full border-2 border-primary/10 animate-ping"
                      style={{ animationDelay: '1s' }}
                    ></div>

                    {/* Main icon container */}
                    <div className="relative w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center">
                      <Phone className="h-8 w-8 text-primary" />
                    </div>
                  </div>
                  <h3 className="text-lg font-medium">Start test call</h3>
                  <p className="text-sm text-muted-foreground max-w-sm">
                    Click the button below to initiate a test call.
                  </p>
                </div>
              </div>

              <form
                className="flex flex-col items-center justify-center w-full px-4 mb-10"
                onSubmit={e => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (agent?.id == null) return;

                  const payload: DispatchAgentRequest = {
                    agentId: agent?.id.toString(),
                    metadata: { ...state },
                    participantName: 'Test User',
                    phoneNumber: state['user-phone-number'] || '',
                  };
                  mutation.mutate(payload);
                }}
              >
                <div className="space-y-4 w-full max-w-sm">
                  <div className="space-y-2">
                    <RequiredLabel htmlFor="user-phone-number" className="text-sm font-medium">
                      Phone Number
                    </RequiredLabel>
                    <Input
                      id="user-phone-number"
                      required={true}
                      value={state['user-phone-number'] || ''}
                      onChange={e =>
                        setState(prev => ({
                          ...prev,
                          ['user-phone-number']: e.target.value,
                        }))
                      }
                      placeholder={`Enter Phone number`}
                    />
                  </div>
                  {agent?.system_prompt?.variables?.map(varName => (
                    <div key={varName} className="space-y-2">
                      <Label htmlFor={`var-${varName}`} className="text-sm font-medium">
                        {varName}
                      </Label>
                      <Input
                        id={`var-${varName}`}
                        value={state[varName] || ''}
                        onChange={e =>
                          setState(prev => ({
                            ...prev,
                            [varName]: e.target.value,
                          }))
                        }
                        placeholder={`Enter ${varName}`}
                      />
                    </div>
                  ))}
                </div>
                <div className="space-y-4 w-full max-w-sm">
                  <Button
                    type="submit"
                    disabled={mutation.isPending}
                    className="w-full mt-4"
                    size="lg"
                  >
                    <Phone className="h-4 w-4" />
                    Start Test Call
                  </Button>
                </div>
              </form>
            </section>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
};
